import {
  forceAwsConnectionServiceStatusChange as forceAwsConnectionServiceStatusChangeMutation,
  refreshAwsConnectionInventory as refreshAwsConnectionInventoryMutation,
  updateAwsConnectionServiceCostSavingRule as updateAwsConnectionServiceCostSavingRuleMutation,
  type AwsConnectionServiceCostSavingRuleInput,
  estimateCostSavings as estimateCostSavingsQuery,
  fetchAwsConnectionServiceCostSavingHistory as fetchAwsConnectionServiceCostSavingHistoryQuery,
  fetchFargatePrices as fetchFargatePricesQuery,
  fetchAwsConnectionClusters as fetchAwsConnectionClustersQuery,
  fetchOverallSavings as fetchOverallSavingsQuery,
  listAwsConnections as listAwsConnectionsQuery,
  registerAwsConnection as registerAwsConnectionMutation,
  verifyAwsConnection as verifyAwsConnectionMutation,
  type EstimateCostSavingInput,
  type EstimateCostSavingsQuery,
  type FetchAwsConnectionServiceCostSavingHistoryQuery,
  type FetchFargatePricesQuery,
  type FargatePriceInput,
  type FetchAwsConnectionClustersQuery,
  type FetchOverallSavingsQuery,
  type ForceAwsConnectionServiceStatusChangeMutation,
  type ListAwsConnectionsQuery,
  type RefreshAwsConnectionInventoryMutation,
  type RegisterAwsConnectionInput,
  type RegisterAwsConnectionMutation,
  type ServiceStatusChangeAction,
  type VerifyAwsConnectionMutation
} from '@shared/runless-api'

const CONTROL_PLANE_GRAPHQL_URL = import.meta.env.VITE_CONTROL_PLANE_GRAPHQL_URL as string

type ApiResult<T> = { ok: true; data: T } | { ok: false; status: number; error: string }

type GraphQLResponse<T> = {
  data?: T
  errors?: Array<{
    message?: string
  }>
}

export type AwsConnectionItem = ListAwsConnectionsQuery['listAwsConnections'][number]
export type AwsConnectionClusterItem = FetchAwsConnectionClustersQuery['fetchAwsConnectionClusters'][number]
export type FargatePriceItem = FetchFargatePricesQuery['fetchFargatePrices'][number]
export type EstimatedSavings = EstimateCostSavingsQuery['estimateCostSavings']
export type AwsConnectionServiceCostSavingHistoryItem =
  FetchAwsConnectionServiceCostSavingHistoryQuery['fetchAwsConnectionServiceCostSavingHistory']['items'][number]
export type OverallSavingsSnapshot = FetchOverallSavingsQuery['fetchOverallSavings']
export type AwsConnectionServiceRuleInput = AwsConnectionServiceCostSavingRuleInput
export type RegisterConnectionInput = RegisterAwsConnectionInput
export type ForceAwsConnectionServiceStatusChangeInput = {
  awsConnectionId: string
  cluster: string
  service: string
  action: ServiceStatusChangeAction
  expectedTaskCount?: number
}

async function graphQLFetch<TData, TVariables>(
  query: string,
  accessToken: string,
  variables?: TVariables
): Promise<ApiResult<TData>> {
  if (!CONTROL_PLANE_GRAPHQL_URL) {
    return {
      ok: false,
      status: 500,
      error: 'VITE_CONTROL_PLANE_GRAPHQL_URL is not configured'
    }
  }

  try {
    const response = await fetch(CONTROL_PLANE_GRAPHQL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: accessToken
      },
      body: JSON.stringify({
        query,
        ...(variables ? { variables } : {})
      })
    })

    const payload = (await response.json()) as GraphQLResponse<TData>
    if (!response.ok || (payload.errors && payload.errors.length > 0) || !payload.data) {
      return {
        ok: false,
        status: response.status,
        error:
          payload.errors
            ?.map(error => error.message)
            .filter(Boolean)
            .join(', ') ?? `HTTP ${response.status}`
      }
    }

    return { ok: true, data: payload.data }
  } catch (error) {
    return {
      ok: false,
      status: 500,
      error: error instanceof Error ? error.message : 'Unexpected request failure'
    }
  }
}

export async function listAwsConnections(accessToken: string): Promise<ApiResult<AwsConnectionItem[]>> {
  const result = await graphQLFetch<ListAwsConnectionsQuery, never>(listAwsConnectionsQuery, accessToken)
  if (!result.ok) {
    return result
  }

  return {
    ok: true,
    data: result.data.listAwsConnections
  }
}

export async function registerAwsConnection(
  input: RegisterConnectionInput,
  accessToken: string
): Promise<ApiResult<RegisterAwsConnectionMutation['registerAwsConnection']>> {
  const result = await graphQLFetch<RegisterAwsConnectionMutation, { input: RegisterConnectionInput }>(
    registerAwsConnectionMutation,
    accessToken,
    { input }
  )

  if (!result.ok) {
    return result
  }

  return {
    ok: true,
    data: result.data.registerAwsConnection
  }
}

export async function verifyAwsConnection(
  awsConnectionId: string,
  accessToken: string
): Promise<ApiResult<VerifyAwsConnectionMutation['verifyAwsConnection']>> {
  const result = await graphQLFetch<VerifyAwsConnectionMutation, { input: { awsConnectionId: string } }>(
    verifyAwsConnectionMutation,
    accessToken,
    {
      input: {
        awsConnectionId
      }
    }
  )

  if (!result.ok) {
    return result
  }

  return {
    ok: true,
    data: result.data.verifyAwsConnection
  }
}

export async function fetchAwsConnectionClusters(
  awsConnectionId: string,
  accessToken: string
): Promise<ApiResult<AwsConnectionClusterItem[]>> {
  const result = await graphQLFetch<FetchAwsConnectionClustersQuery, { input: { awsConnectionId: string } }>(
    fetchAwsConnectionClustersQuery,
    accessToken,
    {
      input: {
        awsConnectionId
      }
    }
  )

  if (!result.ok) {
    return result
  }

  return {
    ok: true,
    data: result.data.fetchAwsConnectionClusters
  }
}

export async function refreshAwsConnectionInventory(
  awsConnectionId: string,
  accessToken: string
): Promise<ApiResult<RefreshAwsConnectionInventoryMutation['refreshAwsConnectionInventory']>> {
  const result = await graphQLFetch<RefreshAwsConnectionInventoryMutation, { input: { awsConnectionId: string } }>(
    refreshAwsConnectionInventoryMutation,
    accessToken,
    {
      input: {
        awsConnectionId
      }
    }
  )

  if (!result.ok) {
    return result
  }

  return {
    ok: true,
    data: result.data.refreshAwsConnectionInventory
  }
}

export async function fetchFargatePrices(
  input: FargatePriceInput,
  accessToken: string
): Promise<ApiResult<FargatePriceItem[]>> {
  const result = await graphQLFetch<FetchFargatePricesQuery, { input: FargatePriceInput }>(
    fetchFargatePricesQuery,
    accessToken,
    { input }
  )

  if (!result.ok) {
    return result
  }

  return {
    ok: true,
    data: result.data.fetchFargatePrices
  }
}

export async function estimateCostSavings(
  input: EstimateCostSavingInput,
  accessToken: string
): Promise<ApiResult<EstimatedSavings>> {
  const result = await graphQLFetch<EstimateCostSavingsQuery, { input: EstimateCostSavingInput }>(
    estimateCostSavingsQuery,
    accessToken,
    { input }
  )

  if (!result.ok) {
    return result
  }

  return {
    ok: true,
    data: result.data.estimateCostSavings
  }
}

export async function updateAwsConnectionServiceCostSavingRules(
  input: AwsConnectionServiceRuleInput[],
  accessToken: string
): Promise<ApiResult<boolean>> {
  const result = await graphQLFetch<
    { updateAwsConnectionServiceCostSavingRule: boolean },
    { input: AwsConnectionServiceRuleInput[] }
  >(updateAwsConnectionServiceCostSavingRuleMutation, accessToken, { input })

  if (!result.ok) {
    return result
  }

  return {
    ok: true,
    data: result.data.updateAwsConnectionServiceCostSavingRule
  }
}

export async function fetchAwsConnectionServiceCostSavingHistory(
  input: {
    awsConnectionId: string
    cluster: string
    service: string
    nextToken?: string | null
    maxResults?: number
  },
  accessToken: string
): Promise<ApiResult<FetchAwsConnectionServiceCostSavingHistoryQuery['fetchAwsConnectionServiceCostSavingHistory']>> {
  const result = await graphQLFetch<
    FetchAwsConnectionServiceCostSavingHistoryQuery,
    {
      input: {
        awsConnectionId: string
        cluster: string
        service: string
        nextToken?: string | null
        maxResults?: number
      }
    }
  >(fetchAwsConnectionServiceCostSavingHistoryQuery, accessToken, { input })

  if (!result.ok) {
    return result
  }

  return {
    ok: true,
    data: result.data.fetchAwsConnectionServiceCostSavingHistory
  }
}

export async function fetchOverallSavings(accessToken: string): Promise<ApiResult<OverallSavingsSnapshot>> {
  const result = await graphQLFetch<FetchOverallSavingsQuery, never>(fetchOverallSavingsQuery, accessToken)

  if (!result.ok) {
    return result
  }

  return {
    ok: true,
    data: result.data.fetchOverallSavings
  }
}

export async function forceAwsConnectionServiceStatusChange(
  input: ForceAwsConnectionServiceStatusChangeInput,
  accessToken: string
): Promise<ApiResult<ForceAwsConnectionServiceStatusChangeMutation['forceAwsConnectionServiceStatusChange']>> {
  const result = await graphQLFetch<
    ForceAwsConnectionServiceStatusChangeMutation,
    { input: ForceAwsConnectionServiceStatusChangeInput }
  >(forceAwsConnectionServiceStatusChangeMutation, accessToken, { input })

  if (!result.ok) {
    return result
  }

  return {
    ok: true,
    data: result.data.forceAwsConnectionServiceStatusChange
  }
}
